import { z } from 'zod';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { archivableFieldsSchema, showLifecycleSchema } from './lifecycle.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import { policySummarySchema } from './policy.schema';
import {
  ARRANGEMENT_LIMITS,
  calendarDateSchema,
  dutyPointPlaceInputSchema,
  dutyPointPlaceSchema,
  instructionInputSchema,
  requirementLinesInputSchema,
  requirementLineSchema,
  shiftInputSchema,
  supportingArrangementInputSchema,
} from './arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE STANDING ORDER — round-the-clock cover (P3b.1).
 *
 *  ⚠️ ROUND-THE-CLOCK IS NOT AN ARRANGEMENT TYPE. IT IS A GENERATOR.
 *
 *  A standing order emits ORDINARY arrangements — one per shift per duty date, each with
 *  exactly one duty underneath — which use the same model, the same status machine, the
 *  same endpoints, the same detail page and the same fill flow P4 will build. `kind`
 *  already discriminates them and its index already ships. There is no parallel
 *  arrangement code path anywhere, and adding one would be the bug.
 *
 *  ⚠️ NO COMMAND ROLES. AT ALL. NOT EVEN AN EMPTY ARRAY.
 *
 *  Round-the-clock captures no supervision: who is in charge at 3 a.m. changes night by
 *  night, so naming it once here would be wrong most nights. It draws from the same policy
 *  pool, and a policy's `isRequired` command roles are an INTAKE rule that this form does
 *  not apply — nothing here may block a standing order for a missing command role.
 *
 *  That is held STRUCTURALLY rather than by an `if`: this file imports nothing
 *  command-shaped and declares nothing command-shaped, so there is no parameter through
 *  which a role could arrive. `standingOrder.service.ts` must not import
 *  `policyCommandRoster` or `unfilledRequiredRoles` either.
 *
 *  ⚠️ "SUBDIVISION" IS ANOTHER WORD FOR DIVISION — confirmed with the client. There is no
 *  new org level, and `divisions[]` is DERIVED from the requirement allocations by one
 *  writer, exactly as an arrangement's `division_org_unit_ids` is. Two ways to name a
 *  division is two lists that can disagree about who has been asked.
 */

/**
 * ⚠️ TWO STATES, AND THERE IS DELIBERATELY NO DRAFT.
 *
 * A standing order exists to generate; a draft that generates nothing is a row nobody can
 * tell from a broken one. An incomplete order is ACTIVE and the generator SKIPS it with a
 * reason the screen can state — see `blockers` on the DTO.
 *
 * ⚠️ AND THERE IS NO UN-END. Reviving after N days would leave a hole in the generated
 * series that nothing backfills, so resuming is a new standing order. An act with no
 * inverse is not a routine edit, which is why `standingOrder.end` is its own capability.
 */
/**
 * ⚠️ [amended D4] IT LIVES HERE NOW, AND THIS IS ITS ONLY CONSUMER.
 *
 * It was `arrangement.schema.ts`'s until D4 made concurrency structural: two places at
 * once is two DUTY POINTS, and a route is one duty point holding an ordered list, so an
 * arrangement has no mode to record and the control that set one is gone. A standing order
 * still has exactly one place list and still needs to say what it means, so the vocabulary
 * moved rather than being deleted — and it is NOT re-exported from `arrangement.schema.ts`,
 * because a re-export is a second name for one thing.
 *
 * ⚠️ A STANDING ORDER IS NEVER `CONCURRENT`. `standingOrder.model.ts` says why: a generated
 * arrangement must carry exactly one duty.
 *
 * `SINGLE` is sequential with a route of one, so generation has two branches rather than
 * three — but the three names are stored, because "one place" and "one team touring one
 * place" are the same duty and a reader should not have to work that out.
 */
export const LOCATION_MODES = ['SINGLE', 'CONCURRENT', 'SEQUENTIAL'] as const;
export type LocationMode = (typeof LOCATION_MODES)[number];

/**
 * ⚠️ THE ONLY WRITER OF `location_mode`.
 *
 * One place is `SINGLE`; two or more with `oneAfterAnother` is a route. A standing order
 * always passes `true` — its places are always one patrol — so the false branch is reached
 * only by stored history.
 */
export const deriveLocationMode = (locationCount: number, oneAfterAnother: boolean): LocationMode =>
  locationCount <= 1 ? 'SINGLE' : oneAfterAnother ? 'SEQUENTIAL' : 'CONCURRENT';

export const STANDING_ORDER_STATUSES = ['ACTIVE', 'ENDED'] as const;
export type StandingOrderStatus = (typeof STANDING_ORDER_STATUSES)[number];

export const STANDING_ORDER_LIMITS = {
  /** One relief pattern. Six is already two more than anyone has described. */
  SHIFTS: 6,
} as const;

// ── Shifts ───────────────────────────────────────────────────────────────────────

/**
 * The shift pattern, chosen PER STANDING ORDER. Not central, not from a master — a naka
 * point runs 8/8/8 and a festival picket runs 12/12, and neither is a property of the
 * commissionerate.
 *
 * ⚠️ THE SAME `shiftInputSchema` THE ARRANGEMENT USES, so the `<dutyDate>#<shiftId>` slot
 * key, the `+1 day` overnight rule and the minted-once id all come from one place. A
 * second shift shape is a second overnight rule waiting to disagree.
 */
export const standingOrderShiftSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string(),
});

// ── The standing order ───────────────────────────────────────────────────────────

/** ⚠️ NO `.default()` ANYWHERE IN THIS BASE — CLAUDE.md §6 [amended P2]. */
export const standingOrderBaseSchema = z.object({
  policyId: objectIdSchema,
  title: z.string().trim().max(200),
  locations: z.array(dutyPointPlaceInputSchema).max(ARRANGEMENT_LIMITS.PLACES_PER_DUTY_POINT),
  shifts: z.array(shiftInputSchema).max(STANDING_ORDER_LIMITS.SHIFTS),
  startDate: calendarDateSchema,
  /**
   * ⚠️ NULL IS THE MODE, NOT A MISSING VALUE. Absent means "runs until the CP team ends it
   * manually", and there is deliberately no second `hasEndDate` column: two columns for one
   * fact makes `{hasEndDate: true, endDate: null}` a document that validates and that
   * nothing can map. The identical argument `occurrences: null` already carries on a
   * recurrence.
   */
  endDate: calendarDateSchema.nullish(),
  /**
   * ONE requirement, applied to EVERY shift. The brief's field is singular and the form
   * asks for it once; a shift that genuinely needs different numbers is edited on its own
   * generated arrangement, which is an ordinary arrangement like any other.
   */
  requirementLines: requirementLinesInputSchema,
  instructions: z.array(instructionInputSchema).max(ARRANGEMENT_LIMITS.INSTRUCTIONS),
  supportingArrangements: z.array(supportingArrangementInputSchema).max(ARRANGEMENT_LIMITS.SUPPORTING),
});

export const createStandingOrderSchema = standingOrderBaseSchema.partial().extend({
  policyId: objectIdSchema,
  title: z.string().trim().min(1, 'Give this order a name').max(200),
  startDate: calendarDateSchema,
  locations: standingOrderBaseSchema.shape.locations.default([]),
  shifts: standingOrderBaseSchema.shape.shifts.default([]),
  requirementLines: standingOrderBaseSchema.shape.requirementLines.default([]),
  instructions: standingOrderBaseSchema.shape.instructions.default([]),
  supportingArrangements: standingOrderBaseSchema.shape.supportingArrangements.default([]),
});

export const updateStandingOrderSchema = standingOrderBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });

export const endStandingOrderSchema = z.object({
  reason: z.string().trim().max(300).optional(),
});

// ── What comes back ──────────────────────────────────────────────────────────────

export const standingOrderSchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  title: z.string(),
  displayTitle: z.string(),
  status: z.enum(STANDING_ORDER_STATUSES),
  policyId: objectIdSchema,
  policyVersionId: objectIdSchema,
  policyVersionNumber: z.number().int().min(1),
  policy: policySummarySchema.nullable(),
  locations: z.array(dutyPointPlaceSchema),
  shifts: z.array(standingOrderShiftSchema),
  startDate: calendarDateSchema,
  endDate: calendarDateSchema.nullable(),
  requirementLines: z.array(requirementLineSchema),
  instructions: z.array(z.object({ type: z.literal('LINE'), text: z.string() })),
  supportingArrangements: z.array(z.object({ personName: z.string(), responsibilities: z.array(z.string()) })),
  /** ⚠️ DERIVED from the allocations by one writer. Nothing on the form sets it. */
  divisions: z.array(orgUnitSummarySchema),
  /**
   * ⚠️ WHY THE GENERATOR WOULD SKIP THIS ORDER, so the screen can say so rather than the
   * operator discovering it as silence. NEVER includes anything about command roles.
   */
  blockers: z.array(z.string()),
  generatedCount: z.number().int().min(0),
  endedAt: z.iso.datetime().nullable(),
  /** [LIFECYCLE] Off the working list. Always `ENDED` — archiving is refused while ACTIVE. */
  ...archivableFieldsSchema.shape,
  archivedFromStatus: z.enum(STANDING_ORDER_STATUSES).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const standingOrderListItemSchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  displayTitle: z.string(),
  status: z.enum(STANDING_ORDER_STATUSES),
  primaryLocationName: z.string().nullable(),
  placeCount: z.number().int().min(0),
  divisions: z.array(orgUnitSummarySchema),
  /**
   * ⚠️ THE SHIFT ROWS THEMSELVES, NOT A SERVER-COMPUTED COVERAGE STRING.
   *
   * The list's coverage column and the builder's coverage bar must never disagree about
   * whether a pattern has a hole in it, and the way to guarantee that is one derivation
   * with two consumers — `web/src/lib/shiftCoverage.ts` — rather than two implementations
   * that agree on the easy cases. Six rows is about 250 bytes.
   */
  shifts: z.array(standingOrderShiftSchema),
  startDate: calendarDateSchema,
  endDate: calendarDateSchema.nullable(),
  generatedCount: z.number().int().min(0),
  /**
   * [LIFECYCLE] The list renders "Archived · was ended" from this pair rather than
   * REPLACING the status — an archived row must still say what it was.
   */
  archivedAt: z.iso.datetime().nullable(),
  archivedFromStatus: z.enum(STANDING_ORDER_STATUSES).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const standingOrderSummarySchema = z.object({
  id: objectIdSchema,
  reference: z.string(),
  displayTitle: z.string(),
  status: z.enum(STANDING_ORDER_STATUSES),
});

// ── The generator's own answers ──────────────────────────────────────────────────

/** One arrangement the generator would create, or has. */
export const generatedArrangementRefSchema = z.object({
  id: objectIdSchema.nullable(),
  reference: z.string().nullable(),
  dutyDate: calendarDateSchema,
  shiftKey: z.string(),
  shiftLabel: z.string(),
  /** When the divisions are asked to name officers for it. */
  fillOpensAt: z.iso.datetime(),
});

/**
 * ⚠️ THE GENERATOR'S REAL ANSWER, NOT AN ESTIMATE.
 *
 * "This will create roughly N arrangements over the next 2 days, rolling" is produced by
 * the SAME pure planning function the job calls. For a single arrangement an estimate that
 * disagreed with reality was a surprise at the publish click; for a standing order it is
 * the only thing standing between an operator and several hundred unintended arrangements.
 */
export const standingOrderPreviewSchema = z.object({
  /** ⚠️ THE SERVER'S horizon, after any clamp — never a constant the client holds. */
  horizonDays: z.number().int().min(1),
  fillOpenLeadHours: z.number().int().min(0),
  windowFrom: calendarDateSchema,
  windowTo: calendarDateSchema,
  /** How many the next run would create, given what already exists. */
  willCreate: z.number().int().min(0),
  alreadyGenerated: z.number().int().min(0),
  /** Per generated arrangement, so the screen can show a day's worth rather than a number. */
  rows: z.array(generatedArrangementRefSchema),
  /** `true` while the order has no end date, which is what "rolling" means on screen. */
  isRolling: z.boolean(),
  blockers: z.array(z.string()),
  warnings: z.array(z.string()),
});

/**
 * ⚠️ WHAT AN EDIT WOULD DO TO WHAT ALREADY EXISTS, BEFORE IT IS SAVED.
 *
 * Future arrangements carrying no assignments are removed and re-created from the new
 * definition by the next tick; anything already carrying assignments is left exactly as it
 * is. An operator has to be able to see which is which before they commit, or an edit is a
 * guess about other people's rosters.
 */
export const standingOrderEditImpactSchema = z.object({
  regenerates: z.array(generatedArrangementRefSchema),
  preserved: z.array(generatedArrangementRefSchema),
});

// ── List query ───────────────────────────────────────────────────────────────────

export const STANDING_ORDER_SORT_FIELDS = ['startDate', 'title', 'createdAt', 'updatedAt'] as const;
export type StandingOrderSortField = (typeof STANDING_ORDER_SORT_FIELDS)[number];

export const listStandingOrdersQuerySchema = createPaginationSchema(STANDING_ORDER_SORT_FIELDS, 'startDate').extend({
  search: z.string().trim().min(1).max(200).optional(),
  status: z.enum(STANDING_ORDER_STATUSES).optional(),
  policyId: objectIdSchema.optional(),
  divisionOrgUnitId: objectIdSchema.optional(),
  /**
   * ⚠️ [LIFECYCLE] TWO AXES, NOT ONE MIXED DROPDOWN. `status` says where the order is in
   * its own life (Running / Ended); `show` says whether it is on the working list at all.
   * An ENDED order is still on the list until somebody archives it, which is the whole
   * distinction between the two verbs.
   */
  show: showLifecycleSchema,
});
